/**
 * Конвейер Фабрики платежей: на каком шаге заявка и итоги шагов. Отбор по шагу — одна функция
 * (`inPipelineStage`) и для счётчиков, и для клика по шагу. До согласования Гендиректором
 * заявка ровно в одном из шагов 1–2; шаги 3–5 — воронка: «Утверждено» — все согласованные,
 * из них «В 1С / в банке» — выгруженные и исполненные, «Факт по выписке» — с оплатой по
 * выписке или исполненные. Модуль без импортов — проверяется node:test (`pipeline.test.mjs`).
 */

/** 1 — на проверке, 2 — для Гендиректора, 3 — утверждено, 4 — в 1С / в банке, 5 — факт по
 *  выписке. */
export type PipelineStage = 'forming' | 'gates_passed' | 'approved' | 'in_bank' | 'executed';

/** Согласованы Гендиректором — шаг 3 и дальше. */
const APPROVED_STATUSES = ['approved', 'exported_1c', 'executed'];
/** Выгружены в 1С — шаг 4 и дальше. */
const IN_BANK_STATUSES = ['exported_1c', 'executed'];

/** Поля заявки Фабрики (`FinPaymentRequestRow`), по которым считается конвейер. */
export interface PipelineRequest {
  status: string;
  gate_contract_status: string;
  gate_primary_doc_status: string;
  gate_budget_status: string;
  gate_account_routing: string;
  amount: number;
  requested_amount: number;
  approved_amount: number;
  cut_amount: number;
  carryover_request_id: string | null;
  paid_amount: number;
  payment_execution_status: string;
}

export interface PipelineMetrics {
  formingCount: number;
  formingAmount: number;
  gatesPassedCount: number;
  gatesPassedAmount: number;
  approvedCount: number;
  approvedAmount: number;
  cutCarryoverTotal: number;
  inBankCount: number;
  inBankAmount: number;
  paidTotal: number;
  partiallyPaidTotal: number;
  unpaidTotal: number;
}

/**
 * Все четыре гейта пройдены без предупреждения: договор и первичка — `passed`, бюджет — в
 * лимите или в допуске 5 %, счёт списания определён. Остальное — предупреждение: договор не
 * привязан или заблокирован (`warning`, `blocked`), заявка вне бюджета или сверх лимита
 * (`unbudgeted`, `over_limit`), на счёте не хватает денег (`insufficient_funds`). Значения
 * гейтов — как их ставит `evaluate_financial_gates` (`financier/models_gates.rs`).
 */
export function gatesClear(r: PipelineRequest): boolean {
  return (
    r.gate_contract_status === 'passed' &&
    r.gate_primary_doc_status === 'passed' &&
    (r.gate_budget_status === 'within_limit' || r.gate_budget_status === 'within_tolerance') &&
    (r.gate_account_routing === 'settlement_ok' ||
      r.gate_account_routing === 'obs_targeted' ||
      r.gate_account_routing === 'tax_reserve_ok')
  );
}

/** По заявке есть оплата по выписке — частичная или полная. */
function paidByStatement(r: PipelineRequest): boolean {
  return (
    (r.paid_amount || 0) > 0 ||
    r.payment_execution_status === 'paid' ||
    r.payment_execution_status === 'partially_paid'
  );
}

/**
 * Самый дальний шаг заявки. 1 — черновик, на финконтроле или «Готово для Гендиректора» с
 * предупреждением хотя бы одного гейта; 2 — «Готово для Гендиректора» без предупреждений или
 * уже у Гендиректора на решении (с предупреждениями и без: Финансиста она прошла);
 * 3 — согласована Гендиректором; 4 — выгружена в 1С; 5 — исполнена или оплачена по выписке.
 * `null` — заявка вне конвейера: отложена или отклонена.
 */
export function pipelineStageOf(r: PipelineRequest): PipelineStage | null {
  switch (r.status) {
    case 'draft':
    case 'in_review':
      return 'forming';
    case 'ready_for_ceo':
      return gatesClear(r) ? 'gates_passed' : 'forming';
    case 'on_ceo_approval':
      return 'gates_passed';
    case 'approved':
    case 'exported_1c':
    case 'executed':
      if (r.status === 'executed' || paidByStatement(r)) return 'executed';
      return r.status === 'exported_1c' ? 'in_bank' : 'approved';
    default:
      return null;
  }
}

/**
 * Заявка на шаге `stage` — для счётчика шага и отбора по клику. Шаги 1–2 — по самому дальнему
 * шагу заявки: до согласования она ровно в одном из них. Шаги 3–5 — воронка: согласованные;
 * выгруженные и исполненные; с оплатой по выписке или исполненные.
 */
export function inPipelineStage(r: PipelineRequest, stage: string): boolean {
  switch (stage) {
    case 'forming':
    case 'gates_passed':
      return pipelineStageOf(r) === stage;
    case 'approved':
      return APPROVED_STATUSES.includes(r.status);
    case 'in_bank':
      return IN_BANK_STATUSES.includes(r.status);
    case 'executed':
      return r.status === 'executed' || paidByStatement(r);
    default:
      return false;
  }
}

/** Итоги шагов по заявкам на экране. Шаги 1–2 — в сумме действующего обязательства, 3–4 —
 *  в утверждённой сумме; оплачено по выписке — по всем заявкам, остаток к оплате — по
 *  согласованным. */
export function computePipelineMetrics(requests: readonly PipelineRequest[]): PipelineMetrics {
  const m: PipelineMetrics = {
    formingCount: 0,
    formingAmount: 0,
    gatesPassedCount: 0,
    gatesPassedAmount: 0,
    approvedCount: 0,
    approvedAmount: 0,
    cutCarryoverTotal: 0,
    inBankCount: 0,
    inBankAmount: 0,
    paidTotal: 0,
    partiallyPaidTotal: 0,
    unpaidTotal: 0,
  };
  for (const r of requests) {
    // Для родительской заявки с перенесённым остатком берём её действующую сумму (approved_amount),
    // сохраняя баланс: родитель (60) + ребёнок -ОСТ (40) = исходное обязательство (100) (N06)
    const isParentWithCarryover = Boolean(
      r.carryover_request_id || (r.cut_amount > 0 && r.approved_amount > 0)
    );
    const amt = isParentWithCarryover ? r.approved_amount : r.requested_amount || r.amount;
    const appr = r.approved_amount || r.amount;
    const approved = inPipelineStage(r, 'approved');

    if (inPipelineStage(r, 'forming')) {
      m.formingCount += 1;
      m.formingAmount += amt;
    } else if (inPipelineStage(r, 'gates_passed')) {
      m.gatesPassedCount += 1;
      m.gatesPassedAmount += amt;
    }
    if (approved) {
      m.approvedCount += 1;
      m.approvedAmount += appr;
      m.cutCarryoverTotal += r.cut_amount || 0;
    }
    if (inPipelineStage(r, 'in_bank')) {
      m.inBankCount += 1;
      m.inBankAmount += appr;
    }

    // Факт по выписке (F18)
    const paid = r.paid_amount || 0;
    if (paid > 0) {
      m.paidTotal += paid;
    }
    if (r.payment_execution_status === 'partially_paid' || (paid > 0 && paid < appr)) {
      m.partiallyPaidTotal += paid;
    }
    if (approved) {
      m.unpaidTotal += Math.max(0, appr - paid);
    }
  }
  return m;
}
